import React, { useEffect, useState } from "react";
import { httpService } from "../../../base/services/httpService.service";
import { useNavigate } from "react-router-dom";
import "./style.scss";
import AuthService from "../../../../app/base/services/authentication.service";
import SortArrows from "../../../components/SortArrows";
import { Button, Space, Table } from "antd";
import { convertTZ, formatDateString } from "../../../base/utils/timezoneConverter";
import { PlusIcon } from "../../../assets/icons";
import AddTicketModal from "./AddTicketModal";
import Filters from "./Filters";
import DeleteTicketModal from "./DeleteTicketModal";
import { useNotification } from "../../../base/hooks/useNotification";
import { useTranslation } from "../../../i18n/LanguageContext";

const STATUS_META = {
   "Waiting For Response": { background: "#FFF9F0", color: "#FC9403" },
   Resolved: { background: "#EEF7F2", color: "#0E9349" }
};
const DEFAULT_STATUS_META = { background: "#DDE9FF", color: "#325899" };

const TrashGlyph = () => (
   <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1.43 5.00h17.14M6.43 5.00v-.71a3.57 3.57 0 0 1 3.57-3.57 3.57 3.57 0 0 1 3.57 3.57v.71M7.86 9.29v5.72M12.14 9.29v5.72M3.57 5.00h12.86v12.86a1.43 1.43 0 0 1-1.43 1.43H5a1.43 1.43 0 0 1-1.43-1.43V5.00Z" />
   </svg>
);

const ArrowGlyph = () => (
   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14M13 6l6 6-6 6" />
   </svg>
);

const headerStyle = {
   backgroundColor: "#DDE9FF",
   color: "#325899",
   fontFamily: "Poppins",
   fontSize: "12px",
   fontWeight: 500,
   lineHeight: "16px"
};

function HelpDeskList() {
   const { t } = useTranslation();
   const { api: notificationApi, notificationContextHolder } = useNotification();
   const [showModal, setShowModal] = useState(false);
   const [ticketType, setTicketType] = useState([]);
   const user = AuthService.getUser();
   const [selectedRowKeys, setSelectedRowKeys] = useState([]);
   const [data, setData] = useState([]);
   const navigate = useNavigate();
   const [pagination, setPagination] = useState({ pageSize: 10, pageNumber: 1 });
   const [statuses, setStatuses] = useState([]);
   const [filters, setFilters] = useState({
      TypeId: "",
      StatusId: ""
   });
   const [deleteTickets, setDeleteTickets] = useState(false);

   const onSelectChange = (selectedRowKeys) => {
      setSelectedRowKeys(selectedRowKeys);
   };

   const openAddTicketModal = () => {
      setShowModal(true);
   };

   const fetchTickets = async (updatedPagination) => {
      try {
         const organizationId = AuthService.getUser()?.organizationId;
         let url = `/tickets?pageSize=${updatedPagination.pageSize}&pageNumber=${updatedPagination.pageNumber}`;

         const queryParams = [];

         // Org-scoped, not workspace-scoped - this list is "our organization's
         // tickets", so it should show every ticket the org has filed across
         // all of its workspaces, not just whichever workspace happens to be
         // selected in session storage right now.
         if (user.roles[0].name !== "SuperAdmin" && organizationId) {
            queryParams.push(`organizationId=${organizationId}`);
         }

         if (filters.TypeId) {
            queryParams.push(`TypeId=${filters.TypeId}`);
         }

         if (filters.StatusId) {
            queryParams.push(`StatusId=${filters.StatusId}`);
         }

         if (queryParams.length > 0) {
            url += `&${queryParams.join("&")}`;
         }
         await httpService.get(url, (res) => {
            setData(res?.data);
            setPagination({
               ...updatedPagination,
               total: res?.metaData?.totalItemCount
            });
         });
      } catch (error) {
         console.log(error);
      }
   };

   useEffect(() => {
      fetchTickets(pagination);
   }, [filters, pagination.pageNumber, pagination.pageSize]);

   const handleFilterChange = (key, value) => {
      setFilters({
         ...filters,
         [key]: value
      });
      setPagination({ ...pagination, pageNumber: 1 });
   };

   const getTicketTypes = async () => {
      try {
         await httpService.get("/Tickets/ticket-type", (res) => {
            const ticketTypeData = res.data.map((type) => ({
               value: type.value,
               label: type.displayName
            }));
            setTicketType(ticketTypeData);
         });
      } catch (error) {
         console.log(error);
      }
   };

   const getTicketStatuses = async () => {
      try {
         await httpService.get("/Tickets/ticket-status", (res) => {
            const statusData = res.data.map((status) => ({
               value: status.value,
               label: status.displayName
            }));
            setStatuses(statusData);
         });
      } catch (error) {
         console.log(error);
      }
   };

   useEffect(() => {
      getTicketTypes();
      getTicketStatuses();
   }, []);

   const handleClick = (id) => {
      const path =
         user.roles[0].name !== "SuperAdmin"
            ? `/workspace/help-desk/details/${id}`
            : `/admin/help-desk/details/${id}`;
      navigate(path);
   };

   const isSuperAdmin = user.roles[0].name === "SuperAdmin";

   const columns = [
      {
         title: <div className="header-table-text">{t("subject-column")}</div>,
         dataIndex: "subject",
         key: "subject",
         width: '20%',
         className: "ellipsis-column",
         sorter: (a, b) => a.subject.localeCompare(b.subject),
         sortIcon: (sortOrder) => <SortArrows sortOrder={sortOrder} />,
         render: (subject) => <span className="table-full-name">{subject}</span>
      },
      {
         title: <div className="header-table-text">{t("type-column")}</div>,
         dataIndex: "type",
         key: "type",
         width: '10%',
         className: "ellipsis-column custom-role-column",
         sorter: (a, b) => a.type.localeCompare(b.type),
         sortIcon: (sortOrder) => <SortArrows sortOrder={sortOrder} />,
         render: (type) => type || "-"
      },
      {
         title: <div className="header-table-text">{t("status-column")}</div>,
         dataIndex: "status",
         key: "status",
         width: '15%',
         sorter: (a, b) => a.status.localeCompare(b.status),
         sortIcon: (sortOrder) => <SortArrows sortOrder={sortOrder} />,
         render: (status) => {
            const meta = STATUS_META[status] || DEFAULT_STATUS_META;
            const label = status === "Resolved" ? t("ticket-status-closed") : status;
            return (
               <span className="ticket-status-wrapper" style={{ background: meta.background }}>
                  <span className="ticket-status-dot" style={{ background: meta.color }} />
                  <span style={{ color: meta.color }}>{label}</span>
               </span>
            );
         }
      },
      // "Creation Date" is admin-only - the org's own ticket list (per design)
      // only shows Subject/Type/Status/Submitted by/Last response.
      isSuperAdmin && {
         title: <div className="header-table-text">{t("creation-date-column")}</div>,
         dataIndex: "submissionDate",
         key: "submissionDate",
         width: '10%',
         className: "ellipsis-column custom-role-column",
         sorter: (a, b) => new Date(a.submissionDate) - new Date(b.submissionDate),
         sortIcon: (sortOrder) => <SortArrows sortOrder={sortOrder} />,
         render: (submissionDate) => (submissionDate ? formatDateString(convertTZ(submissionDate)) : "-")
      },
      {
         title: <div className="header-table-text">{t("submitted-by-column")}</div>,
         dataIndex: "userName",
         key: "userName",
         width: '20%',
         className: "ellipsis-column custom-role-column",
         sorter: (a, b) => a.userName.localeCompare(b.userName),
         sortIcon: (sortOrder) => <SortArrows sortOrder={sortOrder} />,
         render: (userName) => userName || "-"
      },
      // "Organization" is admin-only, for distinguishing which org filed each
      // ticket - meaningless (always the same org) on the org's own list.
      isSuperAdmin && {
         title: <div className="header-table-text">{t("organization-column")}</div>,
         dataIndex: "organizationName",
         key: "organizationName",
         width: '15%',
         className: "ellipsis-column custom-role-column",
         sorter: (a, b) => a.organizationName.localeCompare(b.organizationName),
         sortIcon: (sortOrder) => <SortArrows sortOrder={sortOrder} />,
         render: (organizationName) => organizationName || "-"
      },

      {
         title: <div className="header-table-text">{t("last-response-column")}</div>,
         dataIndex: "lastUpdated",
         key: "lastUpdated",
         width: '15%',
         className: "ellipsis-column custom-role-column",
         sorter: (a, b) => new Date(a.lastUpdated) - new Date(b.lastUpdated),
         sortIcon: (sortOrder) => <SortArrows sortOrder={sortOrder} />,
         render: (lastUpdated) => (lastUpdated ? formatDateString(convertTZ(lastUpdated)) : "-")
      },
      {
         title: "",
         key: "action",
         fixed: "right",
         width: 70,
         render: (text, record) => (
            <Space>
               <span className="ticket-row-icon" onClick={() => handleClick(record.id)}>
                  <ArrowGlyph />
               </span>
            </Space>
         ),
         className: "costum-action-column"
      }
   ].filter(Boolean);

   const rowSelection = {
      selectedRowKeys,
      onChange: onSelectChange,
      columnWidth: 48,
      fixed: "left"
   };

   const components = {
      header: {
         cell: (props) => <th {...props} style={headerStyle} />
      }
   };

   const onPageChange = (pageNumber, pageSize) => {
      const updatedPagination = { ...pagination, pageNumber, pageSize };
      setPagination(updatedPagination);
      // fetchTickets(updatedPagination);
   };

   const hideModal = () => {
      setShowModal(false);
   };

   return (
      <>
         {notificationContextHolder}
         <div className="ticket-list-page">
            <div className="ticket-list-header">
               <p className="ticket-list-title">
                  {user.roles[0].name === "SuperAdmin" ? t("ticket-history-title") : t("help-desk-title")}
               </p>
               <div className="ticket-list-actions">
                  <Button
                     className="as-btn as-btn-secondary"
                     disabled={selectedRowKeys.length === 0}
                     onClick={() => setDeleteTickets(true)}
                  >
                     <TrashGlyph />
                     {t("delete-selected-rows-text")}
                  </Button>
                  {user.roles[0].name !== "SuperAdmin" && (
                     <Button className="as-btn as-btn-primary" onClick={openAddTicketModal}>
                        <img src={PlusIcon} alt="" />
                        {t("add-new-ticket-text")}
                     </Button>
                  )}
               </div>
            </div>

            <div className="ticket-list-section">
               <div className="ticket-list-filters">
                  <Filters
                     onChange={handleFilterChange}
                     ticketTypes={ticketType}
                     statuses={statuses}
                     pagination={pagination}
                     setFilters={setFilters}
                     setPagination={setPagination}
                  />
               </div>
               <Table
                  rowKey="id"
                  columns={columns}
                  dataSource={data}
                  rowSelection={rowSelection}
                  components={components}
                  pagination={{
                     ...pagination,
                     onChange: onPageChange,
                     showSizeChanger: false
                  }}
               />
            </div>
         </div>
         <AddTicketModal
            onOk={() => {
               setShowModal(false);
            }}
            fetchData={() => {
               fetchTickets(pagination);
            }}
            onCancel={hideModal}
            visible={showModal}
            getTicketTypes={getTicketTypes}
            ticketType={ticketType}
         />
         <DeleteTicketModal
            visible={deleteTickets}
            onCancel={() => setDeleteTickets(false)}
            selectedRowKeys={selectedRowKeys}
            setDeleteTickets={setDeleteTickets}
            notificationApi={notificationApi}
            fetchData={() => {
               fetchTickets(pagination);
            }}
            pagination={pagination}
         />
      </>
   );
}
export default HelpDeskList;
